import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Investeringen,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";

type InvesteringenBijzonderhedenFormControls = {
  kiaCaribischDeelVanNederland: FormControl<number | null>;
  investeringsaftrekUitVoorgaandeJaren: FormControl<number | null>;
  rdaAftrek: FormControl<number | null>;
  desinvesteringsbijtelling: FormControl<number | null>;
  investeringsaftrekBijOvernameUitNalatenschap: FormControl<boolean | null>;
};

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-correcties-bijzonderheden',
  templateUrl: './spec-correcties-bijzonderheden.component.html',
  styleUrls: ['./spec-correcties-bijzonderheden.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    AsyncPipe
  ]
})
export class SpecCorrectiesBijzonderhedenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public investeringenBijzonderhedenFg = new FormGroup<InvesteringenBijzonderhedenFormControls>({
    kiaCaribischDeelVanNederland: new FormControl<number | null>(null),
    investeringsaftrekUitVoorgaandeJaren: new FormControl<number | null>(null),
    rdaAftrek: new FormControl<number | null>(null),
    desinvesteringsbijtelling: new FormControl<number | null>(null),
    investeringsaftrekBijOvernameUitNalatenschap: new FormControl<boolean | null>(null),
  });

  ngOnInit(): void {
    this.investeringenBijzonderhedenFg.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.dataService.calculateLatestCorporateTaxReturn().subscribe();
      this.saveForm();
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const specificatie = vpbAangifte.specificatieInvesteringen;
    if (!specificatie) {
      return;
    }

    this.investeringenBijzonderhedenFg.patchValue({
      kiaCaribischDeelVanNederland: specificatie.kiaCaribischDeelVanNederland ?? null,
      investeringsaftrekUitVoorgaandeJaren: specificatie.investeringsaftrekUitVoorgaandeJaren ?? null,
      rdaAftrek: specificatie.rdaAftrek ?? null,
      desinvesteringsbijtelling: specificatie.desinvesteringsbijtelling ?? null,
      investeringsaftrekBijOvernameUitNalatenschap: specificatie.investeringsaftrekBijOvernameUitNalatenschap ?? null,
    }, {emitEvent: false});
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.investeringenBijzonderhedenFg.updateValueAndValidity({emitEvent: false});
    const formValue = this.investeringenBijzonderhedenFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const currentSpecificatie = currentEntity.aangifte.specificatieInvesteringen;

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieInvesteringen: {
            ...currentSpecificatie,
            kiaCaribischDeelVanNederland: formValue.kiaCaribischDeelVanNederland ?? undefined,
            investeringsaftrekUitVoorgaandeJaren: formValue.investeringsaftrekUitVoorgaandeJaren ?? undefined,
            rdaAftrek: formValue.rdaAftrek ?? undefined,
            desinvesteringsbijtelling: formValue.desinvesteringsbijtelling ?? undefined,
            investeringsaftrekBijOvernameUitNalatenschap: formValue.investeringsaftrekBijOvernameUitNalatenschap ?? undefined,
          } as Investeringen,
        },
      } as CorporateTaxEntity;
    });
  }
}
